import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Button, DataGridView, useSub } from "@neumetric/blueprint-lib/components";
import { MascotEval, MascotEval2, MascotEval3 } from "../../components/Assets";
import { ImportRisks } from "./Import";
import { UploadRiskPopup } from "./Upload";
import { RiskResponderGrid } from "./Responders";
import {
  getDepartmentResponders,
  riskActions,
  getRisksList,
  getTotalRisksCount,
  getOrgRisksCount,
  isResponderSetForAllDepts,
} from "../../store";

const PER_PAGE = process.env.REACT_APP_RISKS_PER_PAGE || 11;

const columns = [
  {
    dataIndex: "module_risk_id",
    title: "Risk ID",
    typeRepresentation: "LINK",
    width: "10%",
  },
  {
    dataIndex: "title",
    title: "Title",
    typeRepresentation: "TEXT",
    width: "40%",
  },
  {
    dataIndex: "score",
    title: "Score",
    typeRepresentation: "TEXT",
    width: "6%",
    sortableFlag: true,
    align: { header: "left", cell: "center" },
  },
  {
    dataIndex: "decision_item",
    title: "Decision",
    typeRepresentation: "DOT",
    width: "8%",
    sortableFlag: true,
    filterFlag: true,
  },
  {
    dataIndex: "risk_severity_item",
    title: "Severity",
    typeRepresentation: "DOT",
    width: "8%",
    sortableFlag: true,
    filterFlag: true,
  },
  // NJ24: Status column is required
  {
    dataIndex: "risk_status_item",
    title: "Status",
    typeRepresentation: "DOT",
    width: "8%",
    sortableFlag: true,
    filterFlag: false,
  },
  {
    dataIndex: "department",
    title: "Department",
    typeRepresentation: "TEXT",
    width: "8%",
    filterFlag: true,
  },
  {
    dataIndex: "risk_responders",
    title: "Responder",
    typeRepresentation: "LIST",
    width: "12%",
    sortableFlag: true,
    filterFlag: true,
  },
];

const getPage = () => {
  let currentUrlParams = new URLSearchParams(window.location.search);
  return currentUrlParams.get("page") || "RECENT";
};

export const RegisterTrackerScreen = ({ isArchiveTab }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const _ref = React.useRef(null);
  const [state, setState] = React.useState({
    selectedDialogId: null,
    showResponderGrid: false,
  });

  const _filter = React.useRef({
    defaultPage: getPage(),
    pageNo: getPage(),
    pageSize: PER_PAGE,
    filter: isArchiveTab ? { status: "archived" } : {},
  });

  const getPageCount = () => {
    if (_ref.current) {
      let height = _ref.current.clientHeight;
      height -= 32 + 42; // remove table header and pagination
      let count = Math.floor(height / 42) - 1;
      if (count > PER_PAGE) {
        _filter.current.pageSize = count;
      }
    }
  };

  const deptResponders = useSelector(getDepartmentResponders);
  const risksList = useSelector(getRisksList);
  const totalRisksCount = useSelector(getTotalRisksCount);
  const orgRisksCount = useSelector(getOrgRisksCount);
  const deptRespondersSetForAllDepts = useSelector(isResponderSetForAllDepts);

  React.useEffect(() => {
    if (deptRespondersSetForAllDepts) {
      getPageCount();
      fetchRisks();
    }
  }, [deptRespondersSetForAllDepts]);

  useSub("RISK_FILTER_CHANGED", (data) => {
    const { attribute, selected } = data;
    const filter = { ..._filter.current.filter };
    if (attribute) {
      if ((selected || []).length > 0) {
        filter[attribute] = selected;
      } else {
        delete filter[attribute];
      }
      _filter.current.filter = filter;
      _filter.current.pageNo = 1;
    }
    fetchRisks();
  });

  const fetchRisks = () => {
    const { pageNo, filter, pageSize } = _filter.current;
    dispatch(riskActions.fetchRiskList({ pageNo, filter, pageSize }));
    dispatch(riskActions.setCurrentRisk(null));
  };

  const handlePageChange = (pageNo) => {
    navigate(`${window.location.pathname}?page=${pageNo}`);
    _filter.current = { ..._filter.current, pageNo };
    fetchRisks();
  };

  const loading = deptResponders === null;

  const filteredApplied = _filter.current.filter && Object.keys(_filter.current.filter).length > 0;

  const risksListAvailable = Array.isArray(risksList) && risksList.length > 0;

  const showRiskView = deptRespondersSetForAllDepts && orgRisksCount > 0;

  const getCellSemantics = (column, record) => {
    const sematics = {};
    if (column.dataIndex === "module_risk_id") {
      sematics.link = `${record.guid}`;
    }
    return sematics;
  };

  const showOverlay = !loading && !showRiskView;

  const NoMatchingRisksEmpty = (
    <Row className="w-100" justify="center" align="center" style={{ gap: 60 }}>
      <Col style={{ width: 380 }}>
        <div className="ff-exo2 f17 text-subtitle line-22">
          All the risks have been filtered out!
          <br />
          Update the filters to view active risks…
        </div>
      </Col>

      <MascotEval3 alt="eval" width="170px" height="310px" />
    </Row>
  );
  const showNoMatching = filteredApplied && !risksListAvailable && deptRespondersSetForAllDepts && orgRisksCount > 0;

  return (
    <Col className="w-100 h-100 p-20 mt-10 pos-relative">
      <Col className={`w-100 h-100 o-auto risk-register-grid ${showOverlay ? "is-empty-overlay" : ""}`} ref={_ref}>
        <DataGridView
          columns={columns}
          dataSource={loading ? null : !deptRespondersSetForAllDepts ? [] : risksList}
          getCellSemantics={getCellSemantics}
          pagination={{
            pageSize: _filter.current.pageSize,
            totalCount: totalRisksCount,
            recentFlag: !isArchiveTab,
            defaultPage: _filter.current.defaultPage,
            onPageChange: handlePageChange,
          }}
          // IMPORTANT: when overlay is shown, don't render blueprint empty UI at all
          EmptyComponent={
            showOverlay
              ? "" // prevents DataGridViewWaitCell from showing loader/eval
              : showNoMatching
              ? NoMatchingRisksEmpty
              : "There are no Risks"
          }
        />
      </Col>

      {/* EMPTY STATE OVERLAY */}
      {showOverlay && (
        <Row
          className="w-100 h-100"
          justify="center"
          align="center"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "auto",
          }}
        >
          {deptRespondersSetForAllDepts && !filteredApplied && !risksListAvailable ? (
            <Row className="w-100" justify="center" align="center" style={{ gap: 80 }}>
              {/* Import/Upload block (center-left) */}
              <Col style={{ maxWidth: 380 }}>
                <div className="ff-exo2 f17 text-subtitle line-22">
                  Risk Register is currently empty!
                  <br />
                  Import risks from blueprint or upload known risks…
                </div>

                <Row className="mt-20">
                  <Button
                    icon="ASSIGN"
                    label="Import Risks"
                    onClick={() =>
                      setState((_) => ({
                        ..._,
                        selectedDialogId: "import-risk",
                      }))
                    }
                  />
                  <Button
                    className="ml-12"
                    icon="ASSIGN"
                    label="Upload Risks"
                    onClick={() =>
                      setState((_) => ({
                        ..._,
                        selectedDialogId: "upload-risk",
                      }))
                    }
                  />
                </Row>
              </Col>
              <ImportRisks
                open={state.selectedDialogId === "import-risk"}
                onClose={() =>
                  setState((prevState) => ({
                    ...prevState,
                    selectedDialogId: null,
                  }))
                }
              />

              <UploadRiskPopup
                open={state.selectedDialogId === "upload-risk"}
                onClose={() =>
                  setState((prevState) => ({
                    ...prevState,
                    selectedDialogId: null,
                  }))
                }
              />

              {/* Different mascot (center-right) */}
              <MascotEval2 alt="eval" width="152px" height="300px" />
            </Row>
          ) : // B) BEFORE responders defined
          state.showResponderGrid ? (
            <RiskResponderGrid />
          ) : (
            <Row className="w-100" justify="center" align="center" style={{ gap: 80 }}>
              <Col style={{ maxWidth: 380 }}>
                <div className="ff-exo2 f17 line-22 text-subtitle">
                  Defining <strong>Risk Responders</strong> is <strong>necessary</strong> before importing and uploading
                  risks!
                </div>

                <Button
                  className="mt-20"
                  icon="ASSIGN"
                  label="Define Responders"
                  classes={{ icon: "fs-14 fw-400" }}
                  onClick={() => setState((_) => ({ ..._, showResponderGrid: true }))}
                />
              </Col>

              <MascotEval alt="eval" width="200px" height="310px" />

              <Col style={{ maxWidth: 380 }}>
                <div className="ff-exo2 f17 text-subtitle line-22">
                  Risk Register is currently empty!
                  <br />
                  Import risks from blueprint or upload known risks…
                </div>

                <Row className="mt-20">
                  <Button icon="ASSIGN" classes={{ icon: "fs-14 fw-400" }} label="Import Risks" disabled />
                  <Button
                    className="ml-12"
                    classes={{ icon: "fs-14 fw-400" }}
                    icon="ASSIGN"
                    label="Upload Risks"
                    disabled
                  />
                </Row>
              </Col>
            </Row>
          )}
        </Row>
      )}
    </Col>
  );
};
